// Global Instructions Rule Applied!
// Read shapes for the fund Statements tab (spec A3.1/A3.3): the generic
// IS/BS row stream from view__fund_statement, the partners-capital
// roll-forward rows, the LP capital account and the selector
// options. Amounts are display-signed (IS/BS) or credit-positive
// (roll-forwards); _fmt twins are server-formatted.
//
// An IS/BS row carries one amount per statement column (spec A3.14 stage 2):
// prog__amounts / prog__amounts_fmt are keyed by the column codes the same
// read returns in `columns`; prog__amount is the primary column (year to
// date / historical cost).
//
// The LP capital account is laid out on the statement layout too (spec A3.14
// stage 3): its rows are FundStatementRow rows and its columns (quarter, year
// to date, since inception) FundStatementColumn columns.
import { z } from 'zod'
import { validateWithZodSchema } from '../../core/utils/utilities.ts'

export const FundStatementRowSchema = z
  .object({
    prog__row_kind: z.enum(['header', 'line', 'subtotal', 'total']),
    cfg_acc_statement_sections__code: z.string(),
    cfg_acc_statement_sections__label: z.string(),
    acc_gl_accounts__account_code: z.string().nullish(),
    acc_gl_accounts__account_name: z.string().nullish(),
    // the layout line's stable code: a group's code on its header, line and
    // subtotal rows, a computed line's computed code; null elsewhere
    prog__line_code: z.string().nullish(),
    prog__label: z.string(),
    prog__amount: z.number().nullish(),
    prog__amount_fmt: z.string().nullish(),
    // per column code; null on header rows
    prog__amounts: z.record(z.string(), z.number().nullable()).nullish(),
    prog__amounts_fmt: z.record(z.string(), z.string().nullable()).nullish(),
    prog__indent: z.number()
  })
  .strict()

export type FundStatementRow = z.infer<typeof FundStatementRowSchema>

export const FundStatementColumnSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    // the column's own window (YYYY-MM-DD); window_start is null for a
    // life-to-date column
    window_start: z.string().nullish(),
    window_end: z.string().nullish()
  })
  .strict()

export type FundStatementColumn = z.infer<typeof FundStatementColumnSchema>

export const FundStatementLayoutSchema = z
  .object({
    code: z.string(),
    label: z.string()
  })
  .passthrough()

export type FundStatementLayout = z.infer<typeof FundStatementLayoutSchema>

export const FundStatementMetaSchema = z
  .object({
    code: z.string(),
    label: z.string(),
    balance_basis: z.string().nullish()
  })
  .passthrough()

export type FundStatementMeta = z.infer<typeof FundStatementMetaSchema>

export const FundPartnersCapitalRowSchema = z
  .object({
    fund_commitments__id: z.string().nullish(),
    investors__name: z.string(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__amount_fmt: z.string().nullish(),
    prog__ownership_pct: z.number(),
    prog__opening: z.number(),
    prog__opening_fmt: z.string().nullish(),
    prog__contributions: z.number(),
    prog__contributions_fmt: z.string().nullish(),
    prog__net_income_allocation: z.number(),
    prog__net_income_allocation_fmt: z.string().nullish(),
    prog__distributions: z.number(),
    prog__distributions_fmt: z.string().nullish(),
    // transfers of interest (A3.6): transfer out (-) / in (+) at the transfer
    // date; 0 on ordinary rows. Nullish: the fund-group statements share
    // this schema and do not emit it.
    prog__transfers: z.number().nullish(),
    prog__transfers_fmt: z.string().nullish(),
    prog__ending: z.number(),
    prog__ending_fmt: z.string().nullish(),
    prog__fee_capital_balance: z.number(),
    prog__fee_capital_balance_fmt: z.string().nullish()
  })
  .strict()

export type FundPartnersCapitalRow = z.infer<typeof FundPartnersCapitalRowSchema>

// fund_total carries the same prog__ keys minus the identity columns
export const FundPartnersCapitalTotalSchema = z
  .object({
    prog__opening: z.number(),
    prog__opening_fmt: z.string().nullish(),
    prog__contributions: z.number(),
    prog__contributions_fmt: z.string().nullish(),
    prog__net_income_allocation: z.number(),
    prog__net_income_allocation_fmt: z.string().nullish(),
    prog__distributions: z.number(),
    prog__distributions_fmt: z.string().nullish(),
    prog__transfers: z.number().nullish(),
    prog__transfers_fmt: z.string().nullish(),
    prog__ending: z.number(),
    prog__ending_fmt: z.string().nullish()
  })
  .passthrough()

export type FundPartnersCapitalTotal = z.infer<typeof FundPartnersCapitalTotalSchema>

export const FundLpCapitalAccountHeaderSchema = z
  .object({
    investors__name: z.string(),
    // the transfer date (YYYY-MM-DD) when the statement is a prior holder's
    // tenure that ended with a transfer of interest; null otherwise
    prog__holder_until: z.string().nullish(),
    fund_commitments__amount: z.number().nullish(),
    fund_commitments__amount_fmt: z.string().nullish(),
    prog__ownership_pct: z.number(),
    fund_commitments__management_fee_rate: z.number().nullish(),
    cfg_fund_commitment_statuses__label: z.string().nullish(),
    prog__fee_capital_balance: z.number().nullish(),
    prog__fee_capital_balance_fmt: z.string().nullish(),
    // waived fees (spec A3.9 part 2): headline-rate fees minus the LP's own,
    // net of the fee cross-credit, through the last booked fee period
    // (prog__waived_fees_through). 0 / null for a full-fee LP.
    // prog__waived_fees_repayable (addendum part 1) = the amount OUTSTANDING
    // under the fund's Waived Fee Settlement (owed - returned; 0 when nothing
    // is owed) and prog__waived_fees_settlement that setting's label. On the
    // consolidated statement the amount is the members' added together, the
    // label is set only when the owing members share one, and
    // prog__waived_fees_members lists each member fund's own figures.
    prog__waived_fees_to_date: z.number().nullish(),
    prog__waived_fees_to_date_fmt: z.string().nullish(),
    prog__waived_fees_repayable: z.number().nullish(),
    prog__waived_fees_repayable_fmt: z.string().nullish(),
    prog__waived_fees_settlement: z.string().nullish(),
    prog__waived_fees_members: z
      .array(
        z
          .object({
            funds__id: z.string().nullish(),
            funds__name: z.string().nullish(),
            prog__waived_fees_settlement: z.string().nullish(),
            prog__waived_fees_to_date: z.number().nullish(),
            prog__waived_fees_to_date_fmt: z.string().nullish(),
            prog__waived_fees_repayable: z.number().nullish(),
            prog__waived_fees_repayable_fmt: z.string().nullish()
          })
          .passthrough()
      )
      .nullish(),
    prog__waived_fees_through: z.string().nullish()
  })
  .passthrough()

export type FundLpCapitalAccountHeader = z.infer<typeof FundLpCapitalAccountHeaderSchema>

export const FundStatementTypeOptionSchema = z
  .object({
    cfg_acc_statement_types__code: z.string(),
    cfg_acc_statement_types__label: z.string()
  })
  .strict()

export type FundStatementTypeOption = z.infer<typeof FundStatementTypeOptionSchema>

export const FundStatementLpOptionSchema = z
  .object({
    fund_commitments__id: z.string(),
    // set on the prior-holder entries of a transferred commitment ("<name>
    // (until MM/DD/YYYY)"); null on current-holder entries
    fund_commitment_transfers__id: z.string().nullish(),
    investors__name: z.string(),
    fund_commitments__amount: z.number().nullish(),
    cfg_fund_commitment_statuses__label: z.string().nullish(),
    prog__ownership_pct: z.number(),
    // the Investor Statements picker (one statement per investor): the
    // statement subject this commitment belongs to - the holder's lead
    // commitment, or its lead consolidated commitment on a consolidated
    // fund - and the plain holder name it is listed under; null on the
    // prior-holder entries
    prog__statement_fund_commitment_id: z.string().nullable().optional(),
    prog__statement_label: z.string().nullable().optional()
  })
  .strict()

export type FundStatementLpOption = z.infer<typeof FundStatementLpOptionSchema>

export function validateFundStatementRows(data: unknown): FundStatementRow[] {
  try {
    return validateWithZodSchema(FundStatementRowSchema, data, 'fund statement row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund statement validation failed: ${error}`)
  }
}

export function validateFundStatementColumns(data: unknown): FundStatementColumn[] {
  try {
    return validateWithZodSchema(FundStatementColumnSchema, data, 'fund statement column', { isArray: true })
  } catch (error) {
    throw new Error(`Fund statement columns validation failed: ${error}`)
  }
}

export function validateFundPartnersCapitalRows(data: unknown): FundPartnersCapitalRow[] {
  try {
    return validateWithZodSchema(FundPartnersCapitalRowSchema, data, 'fund partners capital row', { isArray: true })
  } catch (error) {
    throw new Error(`Fund partners capital validation failed: ${error}`)
  }
}

export function validateFundStatementTypeOptions(data: unknown): FundStatementTypeOption[] {
  try {
    return validateWithZodSchema(FundStatementTypeOptionSchema, data, 'fund statement type option', { isArray: true })
  } catch (error) {
    throw new Error(`Fund statement type options validation failed: ${error}`)
  }
}

export function validateFundStatementLpOptions(data: unknown): FundStatementLpOption[] {
  try {
    return validateWithZodSchema(FundStatementLpOptionSchema, data, 'fund statement LP option', { isArray: true })
  } catch (error) {
    throw new Error(`Fund statement LP options validation failed: ${error}`)
  }
}
